import React, { useCallback, useMemo, memo } from 'react';
import { Table, Space, Card, Tooltip, Typography } from 'antd';
import { EyeOutlined, PlusOutlined } from '@ant-design/icons';
import { DeleteIcon, EditIcon } from 'icons';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useRecoilValue } from 'recoil';
import { getPage } from 'hooks/useUser';
import useLandingPages from '../useLandingPages';
import WebsiteFilters from 'components/filters/WebsiteFilters';
import { Button } from 'components';

const { Link } = Typography;

export const LandingPagesList = memo(() => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const pages = useRecoilValue(getPage);

  const {
    landingPages,
    loading,
    total,
    currentPage,
    pageSize,
    filters,
    loadingCompanies,
    updateFilters,
    handlePageChange,
    deleteLandingPage,
    shoppingCenters,
  } = useLandingPages();

  const handleDelete = useCallback(
    async (id) => {
      const success = await deleteLandingPage(id);
      if (success) {
        window.location.reload();
      }
    },
    [deleteLandingPage],
  );

  const handleFiltersChange = useCallback(
    (newFilters) => {
      updateFilters(newFilters);
    },
    [updateFilters],
  );

  const getShoppingCenterDomain = useCallback((shoppingCenter) => {
    if (!shoppingCenter) return null;

    const centerName = (
      shoppingCenter.title ||
      shoppingCenter.name ||
      ''
    ).toLowerCase();

    if (centerName.includes('torino')) return 'torinooutletvillage.com';
    if (centerName.includes('roma')) return 'romaoutletvillage.com';
    if (centerName.includes('sicilia')) return 'siciliaoutletvillage.com';

    if (shoppingCenter.domain) return shoppingCenter.domain;
    if (shoppingCenter.website)
      return shoppingCenter.website.replace(/^https?:\/\//, '');

    return null;
  }, []);

  const buildLandingUrl = useCallback((domain, lang, linkPath) => {
    if (!domain || !linkPath) return null;
    return `https://${domain}/${lang}/landing/${linkPath}`;
  }, []);

  const columns = useMemo(
    () => [
      {
        title: <div style={{ textAlign: 'center' }}>{t('Titolo')}</div>,
        dataIndex: 'title_it',
        key: 'title_it',
        width: 300,
        render: (text) => (
          <span style={{ fontWeight: 500, textAlign: 'left' }}>
            {text || '-'}
          </span>
        ),
        align: 'center',
      },
      {
        title: <div style={{ textAlign: 'center' }}>Link IT</div>,
        key: 'link_it',
        width: 250,
        render: (_, record) => {
          const domain = getShoppingCenterDomain(record.shopping_center);
          if (!domain || !record.link_it) return '-';

          const url = buildLandingUrl(domain, 'it', record.link_it);

          return (
            <Link
              href={url}
              target='_blank'
              rel='noopener noreferrer'
              style={{
                fontFamily: 'monospace',
                fontSize: '11px',
                wordBreak: 'break-all',
              }}
            >
              {url}
            </Link>
          );
        },
        align: 'left',
      },
      {
        title: <div style={{ textAlign: 'center' }}>Link EN</div>,
        key: 'link_en',
        width: 250,
        render: (_, record) => {
          const domain = getShoppingCenterDomain(record.shopping_center);
          if (!domain) return '-';

          const linkPath = record.link_en || record.link_it;
          if (!linkPath) return '-';

          const url = buildLandingUrl(domain, 'en', linkPath);

          return (
            <Link
              href={url}
              target='_blank'
              rel='noopener noreferrer'
              style={{
                fontFamily: 'monospace',
                fontSize: '11px',
                wordBreak: 'break-all',
              }}
            >
              {url}
            </Link>
          );
        },
        align: 'left',
      },
      {
        title: <div style={{ textAlign: 'center' }}>Link FR</div>,
        key: 'link_fr',
        width: 250,
        render: (_, record) => {
          const isTorino = record.shopping_center?.title
            ?.toLowerCase()
            .includes('torino');
          if (!isTorino) return '-';

          const domain = getShoppingCenterDomain(record.shopping_center);
          if (!domain) return '-';

          const linkPath = record.link_fr || record.link_it;
          if (!linkPath) return '-';

          const url = buildLandingUrl(domain, 'fr', linkPath);

          return (
            <Link
              href={url}
              target='_blank'
              rel='noopener noreferrer'
              style={{
                fontFamily: 'monospace',
                fontSize: '11px',
                wordBreak: 'break-all',
              }}
            >
              {url}
            </Link>
          );
        },
        align: 'left',
      },
      {
        title: <div style={{ textAlign: 'center' }}>{t('Outlet Village')}</div>,
        dataIndex: 'shopping_center',
        key: 'shopping_center',
        width: 250,
        align: 'center',
        render: (shoppingCenter) => shoppingCenter?.title || '-',
      },
      {
        title: <div style={{ textAlign: 'center' }}>{t('Data creazione')}</div>,
        dataIndex: 'created_at',
        key: 'created_at',
        width: 150,
        align: 'center',
        render: (date) => {
          if (!date) return '-';
          return new Date(date).toLocaleDateString('it-IT');
        },
      },
      {
        title: <div style={{ textAlign: 'center' }}>{t('Azioni')}</div>,
        key: 'actions',
        width: 180,
        align: 'center',
        render: (_, record) => (
          <Space size='middle'>
            {(pages?.ability?.can_read !== false ||
              record.can_read !== false) && (
              <Tooltip title={t('Visualizza')}>
                <Button
                  type='secondary'
                  icon={<EyeOutlined />}
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/website/landing_pages/${record.id}`);
                  }}
                />
              </Tooltip>
            )}
            {record.can_update !== false && (
              <Tooltip title={t('Modifica')}>
                <Button
                  type='secondary'
                  icon={<EditIcon />}
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/website/landing_pages/edit/${record.id}`);
                  }}
                />
              </Tooltip>
            )}
            {record.can_destroy !== false && (
              <Tooltip title={t('Elimina')}>
                <Button
                  type='red'
                  icon={<DeleteIcon />}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDelete(record.id);
                  }}
                />
              </Tooltip>
            )}
          </Space>
        ),
      },
    ],
    [
      t,
      navigate,
      handleDelete,
      getShoppingCenterDomain,
      buildLandingUrl,
      pages?.ability?.can_read,
    ],
  );

  return (
    <Card
      title={
        <span style={{ fontSize: '24px', fontWeight: '600' }}>
          {t('Landing Pages')}
        </span>
      }
      extra={
        <Button
          type='primary'
          icon={<PlusOutlined />}
          onClick={() => navigate('/website/landing_pages/create')}
          style={{
            marginBottom: 12,
            width: '140px',
            height: 48,
            backgroundColor: '#002637',
            borderColor: '#002637',
          }}
        >
          {t('Nuovo')}
        </Button>
      }
    >
      <WebsiteFilters
        onFiltersChange={handleFiltersChange}
        shoppingCenters={shoppingCenters}
        loading={loadingCompanies}
        filters={filters}
        showInsegna={false}
        showCategorie={false}
        enableSorting={true}
      />
      <Table
        columns={columns}
        dataSource={landingPages}
        loading={loading}
        rowKey='id'
        onRow={(record) => ({
          onClick: () => {
            if (record?.id) {
              navigate(`/website/landing_pages/${record.id}`);
            }
          },
          style: { cursor: 'pointer' },
        })}
        pagination={{
          current: currentPage,
          pageSize: pageSize,
          total: total,
          showSizeChanger: true,
          showQuickJumper: false,
        }}
        onChange={(pagination) => {
          handlePageChange(pagination.current, pagination.pageSize);
        }}
        style={{ marginTop: 8 }}
        locale={{ emptyText: t('Nessun dato trovato') }}
      />
    </Card>
  );
});
